import React, { useMemo, forwardRef } from 'react'
import * as PropTypes from 'prop-types'
import classNames from 'classnames'
import cls from './text.module.scss'
import { useWidth } from '~src/new/app/providers/WidthProvider'
import { light } from '@material-ui/core/styles/createPalette'

/** @typedef {'TITLE_PAGE' | 'BUTTON' | 'BIG_EMPTY_LABEL' | 'LINK' | 'h1' | 'h2' | 'h3' | 'h4' | 'body-large' | 'body-medium' | 'body-base' | 'body-small' | 'body-xsmall'} VariationEnum */

/**
 * A component that renders text with different variants and responsive behavior.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {any} props.children - The content to be rendered inside the Text component.
 * @param {string} [props.className] - Additional CSS class names to be applied to the Text component.
 * @param {React.CSSProperties} [props.style] - Additional inline styles to be applied to the Text component.
 * @param {VariationEnum} [props.variant] - The variant of the text (design-system token, e.g. 'h1', 'body-base').
 * @param {VariationEnum} [props.mobile] - The variant of the text to be used on mobile devices.
 * @param {boolean} [props.bold] - Render with bold font weight.
 * @param {boolean} [props.uppercase] - Transform text to uppercase.
 * @param {'primary' | 'secondary'} [props.color] - Text color from the design-system palette.
 * @param {boolean} [prop.light] - Use light variant.
 * @returns {JSX.Element} The rendered Text component.
 */
const Text = forwardRef((props, refText) => {
  const { children, className, style, variant, mobile, bold, uppercase, color, light, ...extra } =
    props

  const { isMobile } = useWidth()

  const responsiveVariant = useMemo(
    () => (isMobile ? mobile : variant),
    [isMobile, mobile, variant],
  )

  if (!children) return null

  return (
    <p
      className={classNames(
        cls.base,
        cls[responsiveVariant || variant],
        { [cls.bold]: bold, [cls.uppercase]: uppercase, [cls.light]: light },
        color && cls[color],
        className,
      )}
      style={style}
      ref={refText}
      {...extra}
    >
      {children}
    </p>
  )
})

Text.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  style: PropTypes.object,
  variant: PropTypes.oneOf([
    'TITLE_PAGE',
    'BUTTON',
    'BIG_EMPTY_LABEL',
    'LINK',
    'h1',
    'h2',
    'h3',
    'h4',
    'body-large',
    'body-medium',
    'body-base',
    'body-small',
    'body-xsmall',
  ]),

  mobile: PropTypes.oneOf([
    'TITLE_PAGE',
    'BUTTON',
    'BIG_EMPTY_LABEL',
    'LINK',
    'h1',
    'h2',
    'h3',
    'h4',
    'body-large',
    'body-medium',
    'body-base',
    'body-small',
    'body-xsmall',
  ]),

  bold: PropTypes.bool,
  uppercase: PropTypes.bool,
  color: PropTypes.oneOf(['primary', 'secondary']),
  light: PropTypes.bool,
}
export default Text
export { Text }
